import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { FormDirty } from "go-form/components/FormDirty";
import FormMoneyInput from "go-form/components/FormMoneyInput";
import { adminApi } from "../../../../../../../../../services/Api/Admin/adminApi";
import { PayoutInvoiceApi, PayoutInvoiceFormProps } from "../../../../../../../../../services/Api/types";

interface Props {
	isShown: string | null;
	onHide: () => void;
	payoutInvoice: PayoutInvoiceApi | undefined;
	fetchPayoutInvoices: () => Promise<void>;
}

const UpdatePartnerPayoutInvoiceModalForm = ({ isShown, onHide, payoutInvoice, fetchPayoutInvoices }: Props) => {
	const { t } = useTranslation("admin");
	const [loading, setLoading] = useState<boolean>(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const form = useForm<PayoutInvoiceFormProps>({
		criteriaMode: "all",
		defaultValues: {
			total_price: { amount: payoutInvoice?.total_price?.amount, currency: payoutInvoice?.total_price?.currency },
			number: payoutInvoice?.number,
			issued_at: payoutInvoice?.issued_at,
		},
	});

	const {
		formState,
		formState: { errors },
		setError,
		control,
		handleSubmit,
		register,
	} = form;

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		try {
			if (payoutInvoice) {
				await adminApi.updatePartnerInvoice(payoutInvoice.id, data);
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
				await fetchPayoutInvoices();
				onHide();
			}
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<Modal show={!!isShown} onHide={onHide}>
			<FormDirty
				isDirty={formState.isDirty}
				isSubmitting={formState.isSubmitting}
				onSubmit={onSubmit}
				key="update-partner-payout-invoice-form"
			>
				<Modal.Header closeButton>
					<Modal.Title>
						{t("modules.partner_payout_invoice.field.edit_invoice.title")}
						<span className="ms-1">{payoutInvoice?.number}</span>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<FormInput name="number" errors={errors} register={register} label={t("lib:common.word.number")} />
					<FormMoneyInput
						currency={"PLN"}
						errors={errors}
						name="total_price.amount"
						control={control}
						label={t("common.word.total_price")}
					/>
					<FormDatePicker
						errors={errors}
						name="issued_at"
						control={control}
						label={t("modules.partner_payout_invoice.field.issued_at.title")}
					/>
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={loading} onClick={onSubmit}>
						{t("common.action.save", { ns: "lib" })}
					</ButtonLoading>
					<Button variant="light" onClick={onHide}>
						{t("common.action.cancel", { ns: "lib" })}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default UpdatePartnerPayoutInvoiceModalForm;
